import React, { useState, useEffect, useCallback } from 'react';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';
import '../../styles/ExerciseLayout.css';

const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${ASSET_BASE_URL}${url}`;
};

const QuestionRow = ({ question, questionNumber, isExample, answer, handleInputChange, showHint, isReviewMode, isCorrect, isHydrated }) => {
  const questionText = question.question_text.replace(/\(Example\)/gi, '').replace(/\{\{big_gap\}\}/g, '').trim();
  const correctAnswer = question.accepted_answers?.[0];

  const isAnswerCorrect = isCorrect === true || isCorrect === 'true';
  const showIncorrectInReview = isReviewMode && !isAnswerCorrect;

  return (
    <div className={`flex flex-col md:flex-row md:items-center gap-3 py-4 border-b border-slate-100 dark:border-slate-700 last:border-0 ${isExample ? 'opacity-80' : ''}`}>
      {/* Number */}
      <div className="flex items-baseline gap-3 flex-grow min-w-0">
        <span className="w-6 font-bold text-primary-blue text-lg flex-shrink-0 text-right leading-tight">
          {isExample ? '' : `${questionNumber}.`}
        </span>
        <span className="flex-1 font-body font-bold text-slate-600 dark:text-slate-300 min-w-0 leading-tight py-1">
          {questionText}
        </span>
      </div>

      {/* Input */}
      <div className="w-full md:w-auto md:min-w-[10rem] flex-shrink-0 flex items-center gap-2">
        {isExample ? (
          <div className="flex-grow px-3 py-2 bg-slate-50 dark:bg-slate-800/50 rounded-xl border-2 border-transparent font-bold text-primary-blue/60 italic text-center">
            {correctAnswer}
          </div>
        ) : showIncorrectInReview ? (
          <div className="exercise-input-text bg-red-50 px-3 py-1 rounded-lg flex-grow flex items-center min-w-0 h-11 justify-center">
            {question.user_answer?.answer
              ? <span className="text-red-500 line-through decoration-2 mr-2 font-bold">{question.user_answer.answer}</span>
              : <span className="text-gray-400 mr-2 font-bold">__</span>
            }
            <span className="text-green-600 font-bold">{correctAnswer}</span>
          </div>
        ) : (
          <input
            type="text"
            id={question.id}
            autoComplete="off"
            value={showHint || (isReviewMode && isAnswerCorrect) ? correctAnswer : (answer || '')}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            className={`flex-grow bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-600 focus:ring-4 focus:ring-blue-100 focus:border-primary-blue rounded-xl px-4 py-2 font-bold text-primary-blue dark:text-blue-400 transition-all outline-none min-w-0 h-11 text-center ${showHint ? 'hint-input' : ''}`}
            readOnly={showHint || !isHydrated}
            disabled={isReviewMode || !isHydrated}
          />
        )}
        {isReviewMode && (
          <div className="shrink-0 flex items-center gap-2">
            {isAnswerCorrect
              ? <span className="correct-checkmark">✓</span>
              : <span className="incorrect-checkmark">✗</span>
            }
            {!isExample && <QuestionLearnButton question={question} />}
          </div>
        )}
      </div>
    </div>
  );
};

const StartersReadingPart2 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData) {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId || isReviewMode) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach(qId => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated, isReviewMode]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);
    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const { image_url } = exerciseData.content_data;

  const sortedQuestions = [...exerciseData.questions].sort((a, b) => a.order_index - b.order_index);
  const exampleQuestions = sortedQuestions.filter(q => q.order_index === 0);
  const mainQuestions = sortedQuestions.filter(q => q.order_index > 0);

  const handleInputChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  return (
    <div className="w-full h-full p-1 md:px-10">
      <div className="relative flex flex-col items-center h-full bg-transparent rounded-2xl p-2 md:p-6 overflow-y-auto custom-scrollbar">
        {/* Shared image */}
        {image_url && (
          <div className="flex justify-center mb-4">
            <img
              src={resolveUrl(image_url)}
              alt="Exercise illustration"
              className="rounded-xl max-w-full max-h-64 object-contain shadow-sm border border-gray-100 p-1 bg-white"
            />
          </div>
        )}

        <div className="max-w-3xl w-full bg-white dark:bg-slate-800/50 rounded-2xl border-2 border-gray-200 dark:border-slate-600 p-3 md:p-8 shadow-sm">
          <div className="space-y-1">
            {exampleQuestions.map(q => (
              <QuestionRow
                key={q.id}
                question={q}
                isExample={true}
                answer={answers[q.id]}
                handleInputChange={handleInputChange}
                isReviewMode={isReviewMode}
                isCorrect={q.is_correct}
                isHydrated={isHydrated}
              />
            ))}
            {mainQuestions.map((q, index) => (
              <QuestionRow
                key={q.id}
                question={q}
                questionNumber={index + 1}
                isExample={false}
                answer={answers[q.id]}
                handleInputChange={handleInputChange}
                showHint={showHint}
                isReviewMode={isReviewMode}
                isCorrect={q.is_correct}
                isHydrated={isHydrated}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartersReadingPart2;
